import { createHeaders, useServerState } from "@project-mgmt/api-client";
/**
 * Hook to get files for a task
 */
export function useTaskFiles(workspaceId, taskId) {
    return useServerState({
        key: ["files", "task", workspaceId, taskId],
        fetcher: async (data, signal) => {
            const response = await fetch(`/api/files/task/${taskId}`, {
                signal,
                headers: createHeaders(workspaceId),
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch task files: ${response.statusText}`);
            }
            return response.json();
        },
    });
}
/**
 * Hook to prepare file upload
 */
export function useFileUpload(workspaceId) {
    return useServerState({
        key: ["files", "upload", "prepare"],
        fetcher: async (uploadRequest, signal) => {
            const response = await fetch("/api/files/upload", {
                method: "POST",
                signal,
                headers: createHeaders(workspaceId),
                body: JSON.stringify(uploadRequest),
            });
            if (!response.ok) {
                throw new Error(`Failed to prepare upload: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to complete file upload
 */
export function useFileUploadComplete(workspaceId) {
    return useServerState({
        key: ["files", "upload", "complete"],
        fetcher: async (completeData, signal) => {
            const response = await fetch("/api/files/upload/complete", {
                method: "POST",
                signal,
                headers: createHeaders(workspaceId),
                body: JSON.stringify(completeData),
            });
            if (!response.ok) {
                throw new Error(`Failed to complete upload: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to get file download URL
 */
export function useFileDownload(workspaceId) {
    return useServerState({
        key: ["files", "download"],
        fetcher: async (fileId, signal) => {
            const response = await fetch(`/api/files/${fileId}/download`, {
                signal,
                headers: createHeaders(workspaceId),
            });
            if (!response.ok) {
                throw new Error(`Failed to get download URL: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to get file metadata
 */
export function useFileMetadata(fileId, workspaceId) {
    return useServerState({
        key: ["files", "metadata", fileId],
        fetcher: async (data, signal) => {
            const response = await fetch(`/api/files/${fileId}`, {
                signal,
                headers: createHeaders(workspaceId),
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch file metadata: ${response.statusText}`);
            }
            return response.json();
        },
    });
}
/**
 * Hook to delete a file
 */
export function useFileDelete(workspaceId) {
    return useServerState({
        key: ["files", "delete"],
        fetcher: async (fileId, signal) => {
            const response = await fetch(`/api/files/${fileId}`, {
                method: "DELETE",
                signal,
                headers: createHeaders(workspaceId),
            });
            if (!response.ok) {
                throw new Error(`Failed to delete file: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Direct upload function (non-hook)
 */
export async function uploadFileDirect(workspaceId, file, taskId, fileId, onProgress) {
    const uploadUrl = `/api/files/upload/direct/${fileId}`;
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable && onProgress) {
                const progress = (event.loaded / event.total) * 100;
                onProgress(progress);
            }
        });
        xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response);
                }
                catch (error) {
                    reject(new Error("Invalid response format"));
                }
            }
            else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });
        xhr.addEventListener("error", () => {
            reject(new Error("Upload failed due to network error"));
        });
        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader("x-filename", file.name);
        //  added to equal createHeaders function
        xhr.setRequestHeader("X-Workspace-ID", workspaceId);
        xhr.setRequestHeader("x-task-id", taskId);
        xhr.setRequestHeader("content-type", file.type);
        xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem("auth_token")}`);
        xhr.send(file);
    });
}
/**
 * Download file function (non-hook)
 */
export async function downloadFile(workspaceId, fileId) {
    try {
        const response = await fetch(`/api/files/${fileId}/download`, {
            headers: createHeaders(workspaceId),
        });
        if (!response.ok) {
            throw new Error(`Failed to get download URL: ${response.statusText}`);
        }
        const { downloadUrl, filename } = await response.json();
        // Create a temporary link and trigger download
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    catch (error) {
        console.error("Download failed:", error);
        throw error;
    }
}
/**
 * Delete file function (non-hook)
 */
export async function deleteFile(workspaceId, fileId) {
    const response = await fetch(`/api/files/${fileId}`, {
        method: "DELETE",
        headers: createHeaders(workspaceId),
    });
    if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.statusText}`);
    }
}
